import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AiFillFileText, AiFillVideoCamera } from 'react-icons/ai';
import { FaFileDownload } from 'react-icons/fa';
import { MdQuiz } from 'react-icons/md';
import { IoGameController } from 'react-icons/io5';
import { RiFolderFill, RiFolderLockFill, RiFolderOpenFill } from 'react-icons/ri';
import { HiMiniSpeakerWave } from 'react-icons/hi2';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';

import { ProgressInterface } from 'types';
import Tree from './Tree';

type RowProps = {
  item: ProgressInterface;
  parent: string;
  courseID: string;
};

export default function Row({ item, parent, courseID }: RowProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const isFolder = useMemo(() => !!item.items?.length && !!item.folder, [item]);

  const entityIcon = useMemo(() => {
    if (!!item.folder) {
      if (!!item.items?.length) {
        return isCollapsed ? <RiFolderFill /> : <RiFolderOpenFill />;
      } else {
        return <RiFolderLockFill />;
      }
    } else if (!item.folder) {
      if (item.type === 'video') {
        return <AiFillVideoCamera />;
      } else if (item.type === 'text') {
        return <AiFillFileText />;
      } else if (item.type === 'doc') {
        return <FaFileDownload />;
      } else if (item.type === 'quiz') {
        return <MdQuiz />;
      } else if (!!item.type.match(/game_/gi)) {
        return <IoGameController />;
      } else if (item.type === 'audio') {
        return <HiMiniSpeakerWave />;
      } else {
        return <></>;
      }
    }
  }, [isCollapsed, item]);

  const isLocked = !!item.folder && !item.items?.length;

  const metadataContent = () => {
    return (
      <div className="flex items-center w-full gap-3 lg:gap-5">
        <span
          className={`w-10 h-10 lg:w-[55px] lg:h-[55px] rounded-xl flex-shrink-0 flex items-center justify-center text-lg lg:text-2xl
            ${isLocked ? 'bg-gray-200 text-gray-400' : item.folder ? 'bg-purple/20 text-purple' : 'bg-orange/20 text-orange'}`}
        >
          {entityIcon}
        </span>

        <div className="flex-1 min-w-0">
          <span className="block text-sm lg:text-base font-medium text-[#181b1a] truncate">
            {item.title}
          </span>
          {!item.folder && (
            <span className="block text-xs lg:text-sm text-gray-400 font-medium mt-0.5">
              {item.type === 'video' && 'Vídeo'}
              {item.type === 'text' && 'Texto'}
              {item.type === 'doc' && 'Documento'}
              {item.type === 'quiz' && 'Quiz'}
              {!!item.type.match(/game_/gi) && 'Minigame'}
              {item.type === 'audio' && 'Áudio'}
              {' • '}
              <span className={item.percent === 100 ? 'text-[#2bb788]' : ''}>
                {`${Math.round(item.percent)}% concluído`}
              </span>
            </span>
          )}
        </div>

        {isFolder && (
          <span className="text-gray-400 text-sm flex-shrink-0">
            {isCollapsed ? <IoChevronForward /> : <IoChevronDown />}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={`mb-4 pb-3 border-b border-gray-100 last:mb-0 last:border-b-0 last:pb-0`}>
      <div
        className={`${isFolder ? 'cursor-pointer' : ''} ${isLocked ? 'cursor-not-allowed opacity-60' : ''}`}
        onClick={() => (isFolder ? setIsCollapsed(!isCollapsed) : null)}
      >
        {!item.folder && (
          <Link
            to={`/cursos/${courseID}/${parent}/${item._id}`}
            className="block hover:opacity-80 transition-opacity"
          >
            {metadataContent()}
          </Link>
        )}
        {!!item.folder && metadataContent()}
      </div>

      {isFolder && !isCollapsed && (
        <div className="pl-6 lg:pl-10 mt-4 pt-3 border-t border-gray-100">
          <Tree items={item.items as ProgressInterface[]} parent={item._id} courseID={courseID} />
        </div>
      )}
    </div>
  );
}
